import * as THREE from "three";
import { imageCoordToSpherePos } from "../utils";

export const clearScene = (scene: HTMLElement) => {
  while (scene.firstChild) {
    scene.removeChild(scene.firstChild);
  }
};

export const createEdgePlane = (
  scene: HTMLElement,
  start: THREE.Vector3,
  end: THREE.Vector3,
  color: string,
  onClick: () => void,
) => {
  const mid = start.clone().add(end).multiplyScalar(0.5);
  const length = start.distanceTo(end);
  const thickness = 0.01;

  const direction = end.clone().sub(start).normalize();
  const quaternion = new THREE.Quaternion().setFromUnitVectors(
    new THREE.Vector3(1, 0, 0),
    direction,
  );
  const euler = new THREE.Euler().setFromQuaternion(quaternion, "YXZ");

  const plane = document.createElement("a-plane");
  plane.setAttribute("width", length.toString());
  plane.setAttribute("height", thickness.toString());
  plane.setAttribute("color", color);
  plane.setAttribute("material", "shader: flat; side: double");
  plane.setAttribute("position", `${mid.x} ${mid.y} ${mid.z}`);
  plane.setAttribute(
    "rotation",
    `${THREE.MathUtils.radToDeg(euler.x)} ${THREE.MathUtils.radToDeg(euler.y)} ${THREE.MathUtils.radToDeg(euler.z)}`,
  );
  plane.setAttribute("class", "clickable");
  plane.addEventListener("click", onClick);
  scene.appendChild(plane);
  return plane;
};

export const createHitPlane = (
  scene: HTMLElement,
  center: THREE.Vector3,
  width: number,
  height: number,
  rotation: { x: number; y: number; z: number },
  color: string,
  onClick: () => void,
) => {
  const hitPlane = document.createElement("a-plane");
  hitPlane.setAttribute("width", width.toString());
  hitPlane.setAttribute("height", height.toString());
  hitPlane.setAttribute(
    "material",
    `transparent: true; opacity: 0; color: ${color}`,
  );
  hitPlane.setAttribute("position", `${center.x} ${center.y} ${center.z}`);
  hitPlane.setAttribute(
    "rotation",
    `${rotation.x} ${rotation.y} ${rotation.z}`,
  );
  hitPlane.setAttribute("class", "clickable");
  hitPlane.addEventListener("click", onClick);
  scene.appendChild(hitPlane);
  return hitPlane;
};

export const createLabel = (
  scene: HTMLElement,
  text: string,
  color: string,
  position: THREE.Vector3,
  rotation: { x: number; y: number; z: number },
) => {
  const label = document.createElement("a-text");
  label.setAttribute("value", text);
  label.setAttribute("color", color);
  label.setAttribute("align", "left");
  label.setAttribute("side", "double");
  label.setAttribute("scale", "0.3 0.3 0.3");
  label.setAttribute(
    "position",
    `${position.x} ${position.y + 0.04} ${position.z}`,
  );
  label.setAttribute("rotation", `${rotation.x} ${rotation.y} ${rotation.z}`);
  label.setAttribute("opacity", "0");
  scene.appendChild(label);
  return label;
};

export const addHoverHandlers = (
  hitPlane: HTMLElement,
  label: HTMLElement,
  edgePlanes: HTMLElement[],
  color: string,
  isHoverForced: boolean = false,
) => {
  const show = () => {
    hitPlane.setAttribute(
      "material",
      `transparent: true; opacity: 0.4; color: ${color}`,
    );
    label.setAttribute("opacity", "1");
  };

  const hide = () => {
    if (isHoverForced) return; // Don't hide if hover is forced

    hitPlane.setAttribute(
      "material",
      `transparent: true; opacity: 0; color: ${color}`,
    );
    label.setAttribute("opacity", "0");
  };

  edgePlanes.forEach((edgePlane) => {
    edgePlane.addEventListener("mouseenter", show);
    edgePlane.addEventListener("mouseleave", hide);
  });

  hitPlane.addEventListener("mouseenter", show);
  hitPlane.addEventListener("mouseleave", hide);

  if (isHoverForced) {
    show();
  }
};

export const computeBoxGeometry = (
  topLeft: THREE.Vector3,
  topRight: THREE.Vector3,
  bottomLeft: THREE.Vector3,
) => {
  const center = topLeft
    .clone()
    .add(topRight)
    .add(bottomLeft)
    .add(bottomLeft.clone().add(topRight).sub(topLeft)) // effectively bottomRight
    .multiplyScalar(0.25);

  const width = topLeft.distanceTo(topRight);
  const height = topLeft.distanceTo(bottomLeft);

  const direction = topRight.clone().sub(topLeft).normalize();
  const quaternion = new THREE.Quaternion().setFromUnitVectors(
    new THREE.Vector3(1, 0, 0),
    direction,
  );
  const euler = new THREE.Euler().setFromQuaternion(quaternion, "YXZ");

  return {
    center,
    width,
    height,
    rotation: {
      x: THREE.MathUtils.radToDeg(euler.x),
      y: THREE.MathUtils.radToDeg(euler.y),
      z: THREE.MathUtils.radToDeg(euler.z),
    },
  };
};

export const computeEdgeRotation = (
  topLeft: THREE.Vector3,
  topRight: THREE.Vector3,
) => {
  const direction = topRight.clone().sub(topLeft).normalize();
  const quaternion = new THREE.Quaternion().setFromUnitVectors(
    new THREE.Vector3(1, 0, 0),
    direction,
  );
  const euler = new THREE.Euler().setFromQuaternion(quaternion, "YXZ");
  return {
    x: THREE.MathUtils.radToDeg(euler.x),
    y: THREE.MathUtils.radToDeg(euler.y),
    z: THREE.MathUtils.radToDeg(euler.z),
  };
};

export const computeTopCenter = (
  topLeft: THREE.Vector3,
  _topRight: THREE.Vector3,
) => {
  //   const topCenter = topLeft.clone().add(topRight).multiplyScalar(0.5);
  const topCenter = topLeft.clone().multiplyScalar(0.5);
  const outward = topCenter.clone().normalize().multiplyScalar(1.5);
  topCenter.add(outward);
  return topCenter;
};

export const mapBndBoxToSphereCorners = (
  bndbox: { xmin: number; ymin: number; xmax: number; ymax: number },
  imageWidth: number,
  imageHeight: number,
) => {
  const topLeft = imageCoordToSpherePos(
    bndbox.xmin,
    bndbox.ymin,
    imageWidth,
    imageHeight,
  );
  const topRight = imageCoordToSpherePos(
    bndbox.xmax,
    bndbox.ymin,
    imageWidth,
    imageHeight,
  );
  const bottomRight = imageCoordToSpherePos(
    bndbox.xmax,
    bndbox.ymax,
    imageWidth,
    imageHeight,
  );
  const bottomLeft = imageCoordToSpherePos(
    bndbox.xmin,
    bndbox.ymax,
    imageWidth,
    imageHeight,
  );
  return { topLeft, topRight, bottomRight, bottomLeft };
};
